import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import ProductCard from '@/components/ProductCard';
import { EditorialImage } from '@/components/Brand';
import { DeliveryLocationPicker } from '@/components/DeliveryLocationPicker';
import { useDeliveryLocation } from '@/lib/deliveryLocation';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from '@/components/ui/breadcrumb';
import { Package, ChevronRight, ShoppingBasket, RefreshCw, AlertCircle } from 'lucide-react';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';

const REF_IMG = { 'Víveres y Aceites': 'alimentos', 'Cuidado Personal': 'aseo', 'Hogar y Limpieza': 'hogar', 'Bebidas': 'bebidas', 'Combos': 'combos' };
const categoryImage = (category) => category?.image_url || (category && REF_IMG[category.name] ? `/media/reference/category-${REF_IMG[category.name]}.webp` : null);

export default function CategoryLanding() {
  const { id } = useParams();
  const point = useDeliveryLocation();
  const [category, setCategory] = useState(null);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState(false);
  const [products, setProducts] = useState([]);
  const [prodLoading, setProdLoading] = useState(true);
  const [prodError, setProdError] = useState(false);

  const loadCategory = useCallback(() => {
    setCatLoading(true); setCatError(false);
    api.get(`/categories/${id}`).then(({ data }) => setCategory(data)).catch(() => setCatError(true)).finally(() => setCatLoading(false));
  }, [id]);
  useEffect(loadCategory, [loadCategory]);

  const loadProducts = useCallback(() => {
    if (!category?.name) return;
    setProdLoading(true); setProdError(false);
    api.get('/products', { params: { cat: category.name, limit: 200, latitude: point?.latitude, longitude: point?.longitude } })
      .then(({ data }) => setProducts(data)).catch(() => setProdError(true)).finally(() => setProdLoading(false));
  }, [category?.name, point?.latitude, point?.longitude]);
  useEffect(loadProducts, [loadProducts]);

  if (catError) return (
    <main className="max-w-7xl mx-auto px-4 py-10">
      <Alert data-testid="category-landing-error" className="border-brand-orange/40 bg-card"><AlertCircle /><AlertDescription className="mt-2 flex flex-wrap items-center justify-between gap-3">No pudimos cargar esta categoría.<span className="flex gap-2"><Button data-testid="category-landing-retry" onClick={loadCategory} variant="outline" className="gap-2"><RefreshCw /> Reintentar</Button><Button asChild variant="ghost"><Link to="/productos">Ver todos los productos</Link></Button></span></AlertDescription></Alert>
    </main>
  );

  const img = categoryImage(category);
  return (
    <main data-testid="category-landing" className="bg-background pb-12">
      <SEO title={category?.name ? `${category.name}` : 'Categoría'} description={category?.description || `Compra productos de ${category?.name || 'esta categoría'} en Bodegón Mi Barrio y recíbelos en tu barrio.`} image={img || undefined} jsonLd={category ? breadcrumbJsonLd([{ name: 'Inicio', path: '/' }, { name: 'Productos', path: '/productos' }, { name: category.name, path: `/categoria/${category.id}` }]) : undefined} />
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/" data-testid="category-breadcrumb-home">Inicio</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/productos" data-testid="category-breadcrumb-products">Productos</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage data-testid="category-breadcrumb-current">{catLoading ? '…' : category?.name}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Banner */}
      <section data-testid="category-banner" className="max-w-7xl mx-auto px-4 mt-4">
        {catLoading ? <Skeleton className="h-52 w-full rounded-2xl" data-testid="category-banner-loading" /> : (
          <div className="relative overflow-hidden rounded-2xl border border-border bg-brand-navy min-h-52">
            {img && <div className="absolute inset-0"><EditorialImage eager src={img} alt={`Categoría ${category?.name}`} testId="category-banner-image" className="h-full w-full object-cover" /></div>}
            <div className={`absolute inset-0 ${img ? 'bg-gradient-to-r from-brand-navy/90 via-brand-navy/70 to-brand-navy/20' : ''}`} aria-hidden="true" />
            <div className="relative z-10 flex flex-col justify-center gap-3 p-6 sm:p-10 min-h-52 max-w-2xl">
              <span className="inline-flex items-center gap-2 text-brand-orange text-xs font-bold tracking-widest uppercase" data-testid="category-banner-eyebrow"><Package className="h-4 w-4" /> Categoría</span>
              <h1 data-testid="category-banner-title" className="font-heading font-bold text-3xl sm:text-4xl text-brand-cream">{category?.name}</h1>
              {category?.description && <p data-testid="category-banner-description" className="text-brand-cream/85 text-sm sm:text-base leading-relaxed">{category.description}</p>}
              <div className="pt-2"><Button asChild variant="outline" className="bg-transparent border-brand-cream/40 text-brand-cream hover:bg-brand-cream hover:text-brand-navy" data-testid="category-banner-all-link"><Link to="/productos">Ver todo el catálogo <ChevronRight className="h-4 w-4" /></Link></Button></div>
            </div>
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-4 mt-6"><DeliveryLocationPicker /></div>

      {/* Products */}
      <section data-testid="category-products" className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <h2 className="font-heading font-bold text-2xl" data-testid="category-products-title">Productos en {category?.name || 'esta categoría'}</h2>
          <p className="text-sm text-muted-foreground" data-testid="category-products-count">{prodLoading ? 'Consultando…' : `${products.length} productos`} · {point ? 'Disponibles para tu ubicación' : 'Vista previa del catálogo'}</p>
        </div>
        {prodError ? (
          <Alert data-testid="category-products-error"><AlertDescription className="flex flex-wrap items-center justify-between gap-3">No pudimos consultar la disponibilidad.<Button variant="outline" data-testid="category-products-retry" onClick={loadProducts}><RefreshCw className="h-4 w-4" /> Reintentar</Button></AlertDescription></Alert>
        ) : prodLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="category-products-loading">{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>
        ) : products.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="category-products-grid">{products.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        ) : (
          <div className="p-8 bg-card rounded-xl border border-border" data-testid="category-products-empty">
            <ShoppingBasket className="h-8 w-8 text-muted-foreground mb-4" />
            <h3 className="font-body font-bold text-xl">{point ? 'Sin ofertas disponibles en esta ubicación' : 'Aún no hay productos en esta categoría'}</h3>
            <p className="text-sm text-muted-foreground mt-2">{point ? 'Una tienda necesita stock, cobertura y tarifa de entrega para ofrecer productos aquí. Puedes cambiar la ubicación arriba.' : 'Explora el resto del catálogo mientras preparamos esta categoría.'}</p>
            <Button asChild variant="outline" className="mt-4" data-testid="category-products-browse"><Link to="/productos">Ver todos los productos</Link></Button>
          </div>
        )}
      </section>
    </main>
  );
}
